import { useEffect, useRef } from "react";

/**
 * Smart polling — auto-refresh without a manual reload.
 * Fires `onRefresh` on tab focus/visibility and every `intervalMs` while visible;
 * pauses while the tab is hidden. Does NOT fire on mount (caller loads its own data).
 */
export default function useLiveSync(onRefresh, { intervalMs = 15000, enabled = true } = {}) {
  const cbRef = useRef(onRefresh);
  cbRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;
    let timer = null;
    const fire  = () => { if (document.visibilityState === "visible") cbRef.current?.(); };
    const start = () => { stop(); timer = setInterval(fire, intervalMs); };
    const stop  = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => {
      if (document.visibilityState === "visible") { cbRef.current?.(); start(); }
      else stop();
    };
    const onFocus = () => cbRef.current?.();

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    start();
    return () => {
      stop();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs, enabled]);
}
